/**
 * Leadership Burden Diagnostic Page
 *
 * Anxiety-based diagnostic using 10 leadership anxiety measures
 * to assess psychological flexibility and calculate value recovery potential.
 *
 * Features:
 * - Persona selection (Mark Ellison or Sarah Lawson)
 * - Value at stake estimation
 * - 10 anxiety assessments (0-10 scale)
 * - Calculated LPFF from anxiety scores
 * - Combined Recovery Factor (CRF) with persona baseline DCF
 * - ACT coaching multiplier (1.08)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PERSONA_BASELINES,
  calculateLeadershipBurdenV2
} from '../../utils/algorithms';
import DiagnosticHeader from '../../components/diagnostics/DiagnosticHeader';
import '../../styles/diagnostics/index.css';

function LeadershipBurdenDiagnostic() {
  const { code } = useParams(); // GUID from URL
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1=persona, 2=value, 3=anxieties

  const [assessmentData, setAssessmentData] = useState({
    code: code,
    personaId: 'mark', // Default to Mark
    valueAtStake: '',
    scores: {
      anx1: 5, anx2: 5, anx3: 5, anx4: 5, anx5: 5,
      anx6: 5, anx7: 5, anx8: 5, anx9: 5, anx10: 5
    }
  });

  // 10 leadership anxieties
  const anxieties = [
    { id: 1, title: 'Imposter Complex', question: 'How often do you feel like a fraud or fear being exposed as inadequate in your leadership role?' },
    { id: 2, title: 'Fragile Authority', question: 'How often do you question whether people respect your decisions or follow your lead?' },
    { id: 3, title: 'Over-Dependence', question: 'How often do you fear that the organization will collapse or fail without your personal involvement?' },
    { id: 4, title: 'Moral Injury', question: 'How often are you forced to compromise your values or integrity in service of organizational demands?' },
    { id: 5, title: 'Invisible Labour', question: 'How often do you feel your behind-the-scenes efforts go unrecognized or undervalued?' },
    { id: 6, title: 'Failing the Future', question: 'How often do you worry that your transformation efforts will ultimately be wasted or undermined?' },
    { id: 7, title: 'Team Disengagement', question: 'How often does team apathy or disconnection make you feel helpless or ineffective?' },
    { id: 8, title: 'Personal Depletion', question: 'How often do you feel burned out or lacking the energy to sustain your leadership?' },
    { id: 9, title: 'Identity Drift', question: 'How often do you feel like you\'re losing your sense of self or identity in your leadership role?' },
    { id: 10, title: 'Loss of Meaning', question: 'How often do you question the purpose or meaning of your transformation work?' }
  ];

  // GUID validation on mount
  useEffect(() => {
    const checkAssessmentStatus = async () => {
      // Validate GUID format (UUIDv4)
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidPattern.test(code)) {
        setError('Invalid assessment code format');
        setLoading(false);
        return;
      }

      // Check if assessment is already completed
      try {
        const response = await fetch(`/api/get-leadership-burden-results?code=${code}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.results && data.results.completedAt) {
            navigate(`/diagnostics/leadership-burden/results/${code}`);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking assessment status:', error);
      }

      setLoading(false);
    };

    checkAssessmentStatus();
  }, [code, navigate]);

  // Handlers
  const handlePersonaSelect = (personaId) => {
    setAssessmentData(prev => ({ ...prev, personaId }));
  };

  const handleValueChange = (e) => {
    setAssessmentData(prev => ({ ...prev, valueAtStake: e.target.value }));
  };

  const handleScoreChange = (anxId, value) => {
    setAssessmentData(prev => ({
      ...prev,
      scores: { ...prev.scores, [anxId]: parseInt(value) }
    }));
  };

  // Calculate LPFF dynamically
  // eslint-disable-next-line no-unused-vars
  const calculateLPFF = () => {
    const scores = Object.values(assessmentData.scores);
    if (scores.length === 0) return 0;

    const sumScores = scores.reduce((sum, score) => sum + score, 0);
    const lpff = sumScores / 100; // 10 scores × 10 max = 100

    return lpff;
  };

  // Determine zone based on LPFF (aligned with algorithms.js thresholds)
  // eslint-disable-next-line no-unused-vars
  const determineZone = (lpff) => {
    if (lpff < 0.50) return 'Foundational (High Psychological Inflexibility)';
    if (lpff < 0.85) return 'Stretch (Moderate Flexibility)';
    return 'Mastery (High Psychological Flexibility)';
  };

  // Get score color class
  const getScoreColorClass = (score) => {
    if (score >= 0 && score <= 2) return 'score-red';
    if (score >= 3 && score <= 4) return 'score-orange';
    if (score >= 5 && score <= 6) return 'score-amber';
    if (score >= 7 && score <= 8) return 'score-green';
    if (score >= 9 && score <= 10) return 'score-blue';
    return 'score-amber';
  };

  // Submit handler
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const valueAtStake = parseFloat(assessmentData.valueAtStake);

      // Calculate using algorithms v7.0
      const calculations = calculateLeadershipBurdenV2(
        valueAtStake,
        assessmentData.scores,
        assessmentData.personaId
      );

      // Convert scores format from { anx1: 5, anx2: 6 } to { anxiety1: 5, anxiety2: 6 }
      const anxietyScores = {};
      for (let i = 1; i <= 10; i++) {
        anxietyScores[`anxiety${i}`] = assessmentData.scores[`anx${i}`];
      }

      // Prepare payload
      const payload = {
        code: assessmentData.code,
        personaId: assessmentData.personaId,
        valueAtStake: valueAtStake,
        anxietyScores: anxietyScores,
        calculations: {
          totalValueLoss: calculations.totalLoss,
          systemLoss: calculations.systemLoss,
          leadershipLoss: calculations.leadershipLoss,
          dcfBaseline: calculations.dcfBaseline,
          lpffCalculated: calculations.lpffCalculated,
          crf: calculations.crf,
          recoverableValue: calculations.recoverableValue,
          recoveryPoints: calculations.recoveryPoints,
          startingCaptureRateAverage: 0.57,
          finalCaptureRate: calculations.finalCaptureRate,
          maximumRecoveryRate: 0.233,
          mckinseySuccessfulCeiling: calculations.mckinseySuccessfulCeiling
        }
      };

      // POST to API
      const response = await fetch('/api/save-leadership-burden-results', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to save results');
      }

      // Redirect to results
      navigate(`/diagnostics/leadership-burden/results/${assessmentData.code}`);

    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'Failed to submit assessment. Please try again.');
      setLoading(false);
    }
  };

  // Loading state
  if (loading && currentStep === 1) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '48px 16px' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
          <div className="card">
            <p style={{ textAlign: 'center', color: '#697A90' }}>Loading assessment...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && currentStep === 1) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '48px 16px' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
          <div className="card">
            <h1 style={{ color: '#ef4444' }}>Assessment Error</h1>
            <p style={{ color: '#697A90', marginTop: '16px' }}>{error}</p>
            <p style={{ color: '#697A90', marginTop: '8px', fontSize: '14px' }}>
              Please check your assessment link and try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // eslint-disable-next-line no-unused-vars
  const persona = PERSONA_BASELINES[assessmentData.personaId];

  // ============================================
  // STEP 1: Persona Selection
  // ============================================
  if (currentStep === 1) {
    return (
      <>
        <DiagnosticHeader />
        <div className="container">
        {/* Header Card */}
        <div className="card">
          <div className="badge">STEP 1 OF 4</div>
          <h1>Leadership Anxiety Assessment</h1>
          <p className="subtitle">
            Understand how leadership anxieties impact your transformation success
          </p>

          <div className="info-box blue">
            <p><strong>What This Assessment Does:</strong></p>
            <p>This diagnostic measures leadership-specific anxieties that impact both your wellbeing and organizational transformation outcomes. It combines your organizational capability baseline with psychological flexibility assessment to calculate transformation value at risk.</p>
            <p style={{ marginTop: '12px' }}><strong>Important:</strong> This assessment includes transformation value calculations based on your organizational context and leadership anxieties.</p>
          </div>
        </div>

        {/* Psychological Flexibility Explanation */}
        <div className="card">
          <h2>Psychological Flexibility in Leadership</h2>
          <p>
            Leadership anxiety isn't about weakness—it's a natural response to the genuine challenges of transformation work. Psychological flexibility in leadership means staying effective even when difficult thoughts and emotions show up.
          </p>

          <div className="info-box yellow">
            <p><strong>Common Leadership Anxieties:</strong></p>
            <p>• <strong>Imposter Complex:</strong> Fear of being exposed as inadequate</p>
            <p>• <strong>Fragile Authority:</strong> Questioning whether people respect your decisions</p>
            <p>• <strong>Over-Dependence:</strong> Fear the organization will collapse without you</p>
            <p>• <strong>Moral Injury:</strong> Forced to compromise values or integrity</p>
            <p>• <strong>Invisible Labour:</strong> Behind-the-scenes efforts go unrecognized</p>
            <p>• <strong>Failing the Future:</strong> Transformation efforts will be wasted</p>
            <p>• <strong>Team Disengagement:</strong> Team apathy makes you feel helpless</p>
            <p>• <strong>Personal Depletion:</strong> Burned out, lacking energy to sustain leadership</p>
            <p>• <strong>Identity Drift:</strong> Losing sense of self in your role</p>
            <p>• <strong>Loss of Meaning:</strong> Questioning purpose of transformation work</p>
          </div>

          <div className="info-box orange">
            <p><strong>Evidence Base:</strong></p>
            <p>ACT coaching has been shown to have an 8% effect size (η² = 0.08) in improving psychological flexibility and wellbeing outcomes. This multiplier is applied to your Combined Recovery Factor (CRF) to show the potential improvement from ACT-based leadership coaching.</p>
            <p style={{ marginTop: '8px' }}><em>Source: Skews, S. J., 2018, "Acceptance and commitment coaching: A meta-analysis"</em></p>
          </div>
        </div>

        {/* What to Expect */}
        <div className="card">
          <h2>What to Expect</h2>
          <p>This assessment takes approximately 10-15 minutes to complete and consists of four steps:</p>

          <div className="expect-grid">
            <div className="expect-item">
              <h3>Step 1: Leadership Profile</h3>
              <p>Select a persona that matches your organizational context and establish your capability baseline</p>
            </div>

            <div className="expect-item">
              <h3>Step 2: Value at Stake</h3>
              <p>Estimate your annual transformation value to calculate what's at risk from system and leadership burdens</p>
            </div>

            <div className="expect-item">
              <h3>Step 3: Rate Anxieties</h3>
              <p>Assess 10 leadership anxieties based on how frequently you experience them (0 = constantly, 10 = never)</p>
            </div>

            <div className="expect-item">
              <h3>Step 4: Your Results</h3>
              <p>Receive your comprehensive analysis including value at risk, recovery factors, and ACT-based recommendations</p>
            </div>
          </div>
        </div>

        {/* What You'll Learn */}
        <div className="card">
          <h2>What You'll Learn</h2>

          <div className="info-box blue">
            <p><strong>Your Transformation Value Analysis:</strong></p>
            <p>• Total Value at Risk (based on McKinsey research showing 43% average transformation loss)</p>
            <p>• Recoverable Value (what you can realistically bring back)</p>
            <p>• System vs Leadership Loss Breakdown (how much is structural vs psychological)</p>
          </div>

          <div className="info-box yellow">
            <p><strong>Your Psychological Flexibility Profile:</strong></p>
            <p>• Leadership Psychological Flexibility Factor (LPFF) score</p>
            <p>• How your anxiety patterns compare to evidence-based thresholds</p>
            <p>• Which anxieties are most impacting your effectiveness</p>
          </div>

          <div className="info-box orange">
            <p><strong>Actionable Recommendations:</strong></p>
            <p>• ACT-based coaching value (8% effect size from Skews 2018)</p>
            <p>• Specific anxiety reduction strategies</p>
            <p>• Value recovery potential through psychological flexibility improvements</p>
          </div>
        </div>

        {/* 10 Anxieties Overview */}
        <div className="card">
          <h2>The 10 Leadership Anxieties We Assess</h2>
          <p>These anxieties are common across transformation leaders. You'll rate how often each one shows up and pulls you off course.</p>

          <div className="dependencies-grid">
            {anxieties.map((anx) => (
              <div key={anx.id} className="dependency-item">
                <strong>{anx.id}. {anx.title}</strong>
                <p style={{ marginTop: '4px', fontSize: '13px', color: '#697A90' }}>
                  {anx.question}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Privacy */}
        <div className="card">
          <h2>Privacy & Confidentiality</h2>
          <p>Your responses are confidential and used only to generate your personalized diagnostic results. We do not share individual assessment data with third parties.</p>
          <p style={{ marginTop: '12px' }}>This assessment is designed for professional development purposes. If you're experiencing significant mental health concerns, please consult a qualified mental health professional.</p>
        </div>

        {/* Persona Selection */}
        <div className="card">
          <h2>Step 1 - Select Your Leadership Profile</h2>
          <p>Choose the profile that best matches your current role and organizational context.</p>

          <div className="persona-grid">
            {/* Mark Ellison Card */}
            <div
              className={`persona-card ${assessmentData.personaId === 'mark' ? 'selected' : ''}`}
              onClick={() => handlePersonaSelect('mark')}
              role="button"
              tabIndex={0}
              aria-label="Select Mark Ellison persona: Transformation Director with 70% organizational capability baseline"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePersonaSelect('mark');
                }
              }}
            >
              <div className="persona-header">
                <div className="persona-name">Mark Ellison</div>
                {assessmentData.personaId === 'mark' && (
                  <div className="persona-badge">SELECTED</div>
                )}
              </div>
              <div className="persona-role">{PERSONA_BASELINES.mark.role}</div>
              <div className="persona-description">{PERSONA_BASELINES.mark.description}</div>
              <div className="persona-metrics">
                <p><strong>Leadership Strain:</strong> {PERSONA_BASELINES.mark.strain}</p>
                <p><strong>Typical Context:</strong> Enterprise transformation, complex stakeholder landscape, high dependency burden</p>
              </div>
            </div>

            {/* Sarah Lawson Card */}
            <div
              className={`persona-card ${assessmentData.personaId === 'sarah' ? 'selected' : ''}`}
              onClick={() => handlePersonaSelect('sarah')}
              role="button"
              tabIndex={0}
              aria-label="Select Sarah Lawson persona: Chief Operating Officer with 65% organizational capability baseline"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePersonaSelect('sarah');
                }
              }}
            >
              <div className="persona-header">
                <div className="persona-name">Sarah Lawson</div>
                {assessmentData.personaId === 'sarah' && (
                  <div className="persona-badge">SELECTED</div>
                )}
              </div>
              <div className="persona-role">{PERSONA_BASELINES.sarah.role}</div>
              <div className="persona-description">{PERSONA_BASELINES.sarah.description}</div>
              <div className="persona-metrics">
                <p><strong>Leadership Strain:</strong> {PERSONA_BASELINES.sarah.strain}</p>
                <p><strong>Typical Context:</strong> Operational excellence, process improvement, change management</p>
              </div>
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div className="card" style={{ textAlign: 'center' }}>
          <button
            className="btn-primary"
            onClick={() => {
              setCurrentStep(2);
              window.scrollTo(0, 0);
            }}
            aria-label="Continue to Step 2: Value Assessment"
          >
            Continue to Value Assessment
          </button>
        </div>

        {/* Design Note */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • Leadership Burden Diagnostic • v7.0.0 • Step 1 of 4</p>
        </div>
        </div>
      </>
    );
  }

  // ============================================
  // STEP 2: Value Estimation
  // ============================================
  if (currentStep === 2) {
    const value = parseFloat(assessmentData.valueAtStake) || 0;
    const isValid = value >= 0.1 && value <= 1000;

    return (
      <>
        <DiagnosticHeader />
        <div className="container">
        {/* Header Card */}
        <div className="card">
          <div className="badge">STEP 2 OF 4</div>
          <h1>Transformation Value at Stake</h1>
          <p className="subtitle">
            Estimate the annual value (in millions) that your transformation aims to capture or protect.
          </p>
          <div className="persona-info">
            <p><strong>Selected Profile:</strong> {assessmentData.personaId === 'mark' ? 'Mark Ellison' : 'Sarah Lawson'} • {PERSONA_BASELINES[assessmentData.personaId].role}</p>
            <p><strong>Leadership Strain:</strong> {PERSONA_BASELINES[assessmentData.personaId].strain}</p>
          </div>
        </div>

        {/* Value Input */}
        <div className="card">
          <h2>Enter Your Transformation Value</h2>

          <div className="value-input-container">
            <label className="value-label" htmlFor="valueAtStake">
              Transformation Value (millions £)
            </label>
            <input
              type="number"
              id="valueAtStake"
              className="value-input"
              min="0.1"
              max="1000"
              step="0.1"
              value={assessmentData.valueAtStake}
              onChange={handleValueChange}
              placeholder="e.g., 10.5"
              aria-label="Enter transformation value at stake in millions of pounds, between 0.1 and 1000"
              aria-describedby="value-hint-lb"
              aria-required="true"
            />
            <p id="value-hint-lb" className="value-hint">
              Enter a value between £0.1M and £1,000M - this represents the total annual value your transformation is designed to deliver.
            </p>
          </div>

          <div className="examples-box">
            <h3>What to Include in Your Estimate:</h3>
            <ul>
              <li>Revenue growth from new capabilities or market expansion</li>
              <li>Cost savings from process improvements or automation</li>
              <li>Risk mitigation value from modernization initiatives</li>
              <li>Strategic value from improved customer experience</li>
              <li>Operational efficiency gains from digital transformation</li>
              <li>Competitive advantage value from innovation programs</li>
              <li>Compliance and regulatory risk reduction</li>
            </ul>
          </div>

          <div className="info-box blue">
            <p><strong>Research Context:</strong></p>
            <p>Based on McKinsey research (2021), transformations lose an average of 43% of planned value. This assessment helps you understand how much of that loss is recoverable through system improvements and leadership psychological flexibility.</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="card">
          <div className="button-group">
            <button
              className="btn-back"
              onClick={() => {
                setCurrentStep(1);
                window.scrollTo(0, 0);
              }}
              aria-label="Go back to Step 1: Persona Selection"
            >
              Back to Introduction
            </button>
            <button
              className="btn-primary"
              onClick={() => {
                setCurrentStep(3);
                window.scrollTo(0, 0);
              }}
              disabled={!isValid}
              aria-label="Continue to Step 3: Anxiety Assessment"
              aria-disabled={!isValid}
            >
              Continue to Leadership Analysis
            </button>
          </div>
        </div>

        {/* Design Note */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • Leadership Burden Diagnostic • v7.0.0 • Step 2 of 4</p>
        </div>
        </div>
      </>
    );
  }

  // ============================================
  // STEP 3: Anxiety Assessment
  // ============================================
  if (currentStep === 3) {
    return (
      <>
        <DiagnosticHeader />
        <div className="container">
        {/* Header Card */}
        <div className="card">
          <div className="badge">STEP 3 OF 4</div>
          <h1>Leadership Burden Diagnostic</h1>
          <p className="subtitle">
            Assess leadership burdens and calculate your psychological flexibility
          </p>
          <div className="persona-info">
            <p><strong>Selected Profile:</strong> {assessmentData.personaId === 'mark' ? 'Mark Ellison' : 'Sarah Lawson'} • {PERSONA_BASELINES[assessmentData.personaId].role}</p>
            <p><strong>Leadership Strain:</strong> {PERSONA_BASELINES[assessmentData.personaId].strain}</p>
          </div>
        </div>

        {/* Scoring Guide */}
        <div className="scoring-guide">
          <div className="scoring-header">
            <div className="info-icon">i</div>
            <h2>Flexibility-Based Scoring Guide (0-10 Scale)</h2>
          </div>

          <div className="bands-container">
            <div className="band band-0-2">
              <div className="range">0-2</div>
              <div className="label">High Inflexibility</div>
              <div className="description">(constantly experience anxiety)</div>
            </div>

            <div className="band band-3-4">
              <div className="range">3-4</div>
              <div className="label">Frequent</div>
              <div className="description">(often feel this anxiety)</div>
            </div>

            <div className="band band-5-6">
              <div className="range">5-6</div>
              <div className="label">Moderate</div>
              <div className="description">(sometimes experience this)</div>
            </div>

            <div className="band band-7-8">
              <div className="range">7-8</div>
              <div className="label">Adaptive</div>
              <div className="description">(occasionally noticed, rarely hooks)</div>
            </div>

            <div className="band band-9-10">
              <div className="range">9-10</div>
              <div className="label">Flexible</div>
              <div className="description">(thoughts present but don't control)</div>
            </div>
          </div>

          <div className="usage-note">
            <strong>How to use:</strong> Move each slider to indicate how often each of the following shows up and pulls you off course.
            <span className="red"> 0 = Constantly experiencing this anxiety</span>,
            <span className="blue"> 10 = Never experiencing this anxiety</span>.
          </div>
        </div>

        {/* Anxiety Items */}
        <div className="card">
          <h2>Rate All 10 Leadership Anxieties</h2>

          {anxieties.map((anx) => {
            const anxId = `anx${anx.id}`;
            const score = assessmentData.scores[anxId];
            const colorClass = getScoreColorClass(score);

            return (
              <div key={anx.id} className="dependency-item">
                <div className="dependency-header">
                  <div className="dependency-title">
                    <h3>{anx.id}. {anx.title}</h3>
                    <p className="dependency-question">{anx.question}</p>
                  </div>
                  <div className="dependency-score">
                    <span className={`value ${colorClass}`}>{score}</span>
                    <span className="max">/10</span>
                  </div>
                </div>
                <div className="slider-container">
                  <input
                    type="range"
                    id={`slider-${anxId}`}
                    min="0"
                    max="10"
                    value={score}
                    onChange={(e) => handleScoreChange(anxId, e.target.value)}
                    aria-label={`${anx.title}: ${anx.question} Rate from 0 (high inflexibility) to 10 (flexible and adaptive)`}
                    aria-valuemin="0"
                    aria-valuemax="10"
                    aria-valuenow={score}
                    aria-valuetext={`${score} out of 10: ${
                      score >= 0 && score <= 2 ? 'High inflexibility' :
                      score >= 3 && score <= 4 ? 'Frequent anxiety' :
                      score >= 5 && score <= 6 ? 'Moderate flexibility' :
                      score >= 7 && score <= 8 ? 'Adaptive' :
                      'Flexible'
                    }`}
                  />
                  <div className="slider-labels" role="presentation" aria-hidden="true">
                    <span className="label-red">0-2<br/>High Inflexibility</span>
                    <span className="label-orange">3-4<br/>Frequent</span>
                    <span className="label-amber">5-6<br/>Moderate</span>
                    <span className="label-green">7-8<br/>Adaptive</span>
                    <span className="label-blue">9-10<br/>Flexible</span>
                  </div>
                </div>
              </div>
            );
          })}

        </div>

        {/* Navigation */}
        <div className="card">
          <div className="button-group">
            <button
              className="btn-back"
              onClick={() => {
                setCurrentStep(2);
                window.scrollTo(0, 0);
              }}
              aria-label="Go back to Step 2: Value Assessment"
            >
              Back to Value at Stake
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
              aria-label={loading ? 'Submitting assessment data' : 'Complete assessment and generate results'}
              aria-disabled={loading}
            >
              {loading ? 'Generating Results...' : 'Generate Results'}
            </button>
          </div>

          {error && (
            <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px' }}>
              <p style={{ color: '#dc2626', fontSize: '14px' }}>
                <strong>Error:</strong> {error}
              </p>
            </div>
          )}
        </div>

        {/* Design Note */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • Leadership Burden Diagnostic • v7.0.0 • Step 3 of 4</p>
        </div>
        </div>
      </>
    );
  }

  return null;
}

export default LeadershipBurdenDiagnostic;
